import {MenuItem} from '@szhsin/react-menu'
import classNames from 'classnames'
import {useMemo, useState} from 'react'
import {ArrowDownIcon, LoadMoreIcon, MaximizeIcon, SettingsIcon} from '../../../icons'
import {convertTimeStampToHourUTC} from '../../../utils/date'
import {ImgButton} from '../../core/button'
import Dropdown from '../../core/dropdown'
import Switch from '../../core/switch'
import './index.scss'
import {colors} from '../../../utils/colors'
import TextHelper from '../../../utils/text'
import {flattenKeys, getNestedValue} from '../../../utils/object'
import Modal from '../../core/modal'
import JsonView from '@uiw/react-json-view'
import {monokaiTheme} from '@uiw/react-json-view/monokai'
import CopyButton from '../../core/copyButton'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'

const isJsonValueEmpty = value => value === undefined ||
value === null ||
(typeof value === 'string' && value.trim() === '')

const defaultVisibleKeys = ['timestamp', 'message']

export const flattenObjectWithValues = (obj, flattenArrays = true) => {
    const keys = flattenKeys(obj, flattenArrays)
    const flat = {}

    keys.forEach((key) => {
        const value = getNestedValue(obj, key)
        if (!isJsonValueEmpty(value)) {
            flat[key] = value
        }
    })

    return flat
}

const FlattenedRenderJSON = ({data, openModal, expandJsonText}) => {
    const flattened = flattenObjectWithValues(data)

    const entries = Object.entries(flattened)
    const extraEntries = entries.filter(([key]) => key.startsWith('extra.'))
    const otherEntries = entries.filter(([key]) => !key.startsWith('extra.'))

    const sortedExtra = extraEntries.sort(([a], [b]) => a.localeCompare(b))
    const visibleExtra = sortedExtra.length > 0 ? [sortedExtra[0]] : []

    const displayEntries = [...otherEntries, ...visibleExtra]

    return (
        <div>
            {displayEntries.map(([key, value]) => (
                <div key={key} className="key-value-pair">
                    <span className="json-key" title={key}>
                        {TextHelper.textLengthChecker(key, 20)}:
                    </span>
                    <span className="json-value">
                        {String(value).length > 80
                            ? TextHelper.textLengthChecker(value, 80)
                            : value}
                    </span>
                </div>
            ))}
            <ImgButton className="expand-json-btn" onClick={openModal}>
                <span className="maximize-icon"><MaximizeIcon /></span>
                {expandJsonText}
            </ImgButton>
        </div>
    )
}

const LogsList = ({logsData, isLoadMoreEnabled, isDarkMode, onLoadMore, isLoadMoreButtonLoading}) => {
    const text = useNamespaceTranslations('log-management')
    const allColumns = {
        level: {key: 'level', name: text.level, isDisabled: true},
        timestamp: {key: 'timestamp', name: text.timestamp},
        service: {key: 'service', name: text.service},
        host: {key: 'host', name: 'Host'},
        message: {key: 'message', name: text.message}
    }

    const [expandedJsonModalLog, setExpandedJsonModalLog] = useState(null)
    const [visibleKeys, setVisibleKeys] = useState(defaultVisibleKeys)
    const [hoveredLevelIndex, setHoveredLevelIndex] = useState(null)
    const [expandedRowIndex, setExpandedRowIndex] = useState(null)
    const columnsArr = useMemo(() => Object.values(allColumns), [allColumns])

    const toggleColumn = (key) => {
        setVisibleKeys(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        )
    }

    const toggleExpandRow = (index) => {
        setExpandedRowIndex(expandedRowIndex === index ? null : index)
    }

    const handleHoveredIndex = (index) => hoveredLevelIndex === index ? setHoveredLevelIndex(null) : setHoveredLevelIndex(index)
    const handleModal = jsonLog => setExpandedJsonModalLog(jsonLog)

    const visibleColumns = columnsArr.filter(col => visibleKeys.includes(col.key))

    return (
        <>
            {expandedJsonModalLog && <Modal
                isOpen={expandedJsonModalLog}
                contentClassName="overflow-auto"
                id="large-modal"
                title={text.copy_info}
                closeModal={() => setExpandedJsonModalLog(null)}
            >
                <div className={classNames('json-modal-body', {
                    dark: isDarkMode
                })}>
                    <JsonView
                        value={expandedJsonModalLog}
                        style={monokaiTheme}
                        displayObjectSize={false}
                        enableClipboard={false}
                        shortenTextAfterLength={false}
                        className='json-view'
                        displayDataTypes={false}
                    />
                    <CopyButton text={JSON.stringify(expandedJsonModalLog)}/>
                </div>
            </Modal>}
            <div className='log-group-list-container'>
                <div className={classNames('table-container', {
                    dark: isDarkMode
                })}>
                    <div className={classNames('settings-column', {
                        dark: isDarkMode
                    })}>
                        <Dropdown
                            menuButton={
                                <button className="table-settings-button">
                                    <SettingsIcon width={28} height={28} stroke={isDarkMode ? colors.colorFilterTitle : colors.colorMuted}/>
                                </button>
                            }
                        >
                            <span className='dropdown-title'>{text.preferences}</span>
                            <div className='selection-controls'>
                                <button
                                    className={
                                        classNames('selection-controls-btn btn-select-all', {
                                            active: columnsArr.every(col => visibleKeys.includes(col.key))
                                        })
                                    }
                                    onClick={() => setVisibleKeys(columnsArr.filter(col => !col?.isDisabled).map(col => col.key))}>
                                    {text.select_all}
                                </button>
                                <button
                                    className={classNames('selection-controls-btn btn-deselect-all', {
                                        active: visibleKeys?.length === 0
                                    })}
                                    onClick={() => setVisibleKeys([])}>
                                    {text.deselect_all}
                                </button>
                            </div>
                            {columnsArr.map(col => (
                                <>
                                    <MenuItem key={col.key} onClick={(e) => { e.keepOpen = true }}>
                                        <span>{col.name}</span>
                                        <Switch
                                            checked={visibleKeys.includes(col.key) || col.key === allColumns.level.key}
                                            onChange={() => toggleColumn(col.key)}
                                            isDisabled={col?.isDisabled}
                                            classname={classNames({
                                                'disabled-switch': col?.isDisabled
                                            })}/>
                                    </MenuItem>
                                </>
                            ))}
                        </Dropdown>
                    </div>
                    <table className={classNames('group-logs-table', {
                        dark: isDarkMode
                    })}>
                        <thead>
                            <tr>
                                {visibleColumns.map(col => (
                                    <th key={col.key}>
                                        <span className="column-header">{col.name}</span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {logsData.map((log, rowIndex) => (
                                <tr key={rowIndex} className={classNames({
                                    'expanded-row': rowIndex === expandedRowIndex
                                })}>
                                    <span className='table-row' onClick={() => toggleExpandRow(rowIndex)}>
                                        <td key={rowIndex} className={classNames('log-level', {
                                            'w-100': visibleKeys?.length === 0
                                        })}>
                                            {log?.level && <span
                                                className={classNames(`level-badge level-badge-${log.level}`, {
                                                    'expanded-level': hoveredLevelIndex === rowIndex
                                                })}
                                                onMouseEnter={() => handleHoveredIndex(rowIndex)}
                                                onMouseLeave={() => handleHoveredIndex(null)}
                                            >
                                                {hoveredLevelIndex === rowIndex ? log.level : ''}
                                            </span>}
                                            {visibleKeys?.length === 0 &&
                                        <span className={classNames('expand-button', {
                                            'all-columns-deselected': visibleKeys?.length === 0
                                        })}>
                                            <span className='expand-row-icon'>
                                                <ArrowDownIcon stroke={colors.colorGray} width={16} height={16}/>
                                            </span>
                                        </span>}
                                        </td>
                                        {visibleColumns.map((col, colIndex) => {
                                            const value = log[col.key]
                                            const displayValue =
                                    col.key === allColumns.timestamp.key && value
                                        ? convertTimeStampToHourUTC(new Date(value))
                                        : value || '-'

                                            return (
                                                <td key={colIndex} className={classNames(col.key, {
                                                    'w-100': colIndex === visibleColumns?.length - 1
                                                })}>
                                                    <div className="row-body">{displayValue}</div>
                                                    {colIndex === visibleColumns?.length - 1 &&
                                        <span className='expand-button'>
                                            <span className='expand-row-icon'>
                                                <ArrowDownIcon stroke={colors.colorGray} width={16} height={16}/>
                                            </span>
                                        </span>}
                                                </td>
                                            )
                                        })}
                                    </span>
                                    {rowIndex === expandedRowIndex &&
                                <div className='expanded-json-block'>
                                    {<FlattenedRenderJSON data={log} openModal={() => handleModal(log)} expandJsonText={text.expand_json}/>}
                                </div>
                                    }
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {isLoadMoreEnabled && (
                    <ImgButton
                        onClick={onLoadMore}
                        type="button"
                        className={classNames('load-button', {
                            active: isLoadMoreButtonLoading
                        })}
                    >
                        {isLoadMoreButtonLoading && <LoadMoreIcon stroke={colors.colorMuted} />}
                        {text.load_more}
                    </ImgButton>
                )}
            </div>
        </>
    )
}

export default LogsList
